import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/api/client";
import type {
  ExecutionStep,
  Message,
  StepsResponse,
  Thread,
  ThreadCreate,
  ThreadDetail,
  ThreadTodosResponse,
  ThreadUpdate,
} from "@/api/types";

const KEY = ["threads"] as const;
const KEY_ITEM = (threadId: string) => ["thread", threadId] as const;
const KEY_MSGS = (threadId: string) => ["thread-messages", threadId] as const;
const KEY_STEPS = (threadId: string) => ["thread-steps", threadId] as const;
const KEY_TODOS = (threadId: string) => ["thread-todos", threadId] as const;

export function useThreadTodos(threadId: string | undefined) {
  return useQuery({
    queryKey: threadId ? KEY_TODOS(threadId) : ["thread-todos", "noop"],
    queryFn: () =>
      api.GET<ThreadTodosResponse>(`/threads/${threadId}/todos`),
    enabled: Boolean(threadId),
    staleTime: Infinity,
  });
}

interface ThreadsFilters {
  agent_id?: string;
  team_session_id?: string;
  include_sandbox?: boolean;
  pinned_only?: boolean;
}

export function useThreads(filters: ThreadsFilters = {}) {
  return useQuery({
    queryKey: [...KEY, filters],
    queryFn: () => {
      const params: Record<string, string | boolean> = {};
      if (filters.agent_id) params.agent_id = filters.agent_id;
      if (filters.team_session_id)
        params.team_session_id = filters.team_session_id;
      if (filters.include_sandbox !== undefined)
        params.include_sandbox = filters.include_sandbox;
      if (filters.pinned_only) params.pinned_only = true;
      return api.GET<Thread[]>("/threads", { params });
    },
  });
}

export function usePinThread() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      threadId,
      pinned,
    }: {
      threadId: string;
      pinned: boolean;
    }) => api.PATCH<Thread>(`/threads/${threadId}/pin`, { pinned }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: KEY });
      void qc.invalidateQueries({ queryKey: KEY_ITEM(vars.threadId) });
    },
  });
}

export function useThread(threadId: string) {
  return useQuery({
    queryKey: KEY_ITEM(threadId),
    queryFn: () => api.GET<ThreadDetail>(`/threads/${threadId}`),
    enabled: Boolean(threadId),
  });
}

export function useCreateThread() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: ThreadCreate) =>
      api.POST<Thread>("/threads", payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

export function useUpdateThread(threadId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: ThreadUpdate) =>
      api.PATCH<Thread>(`/threads/${threadId}`, patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
      void qc.invalidateQueries({ queryKey: KEY_ITEM(threadId) });
    },
  });
}

export function useDeleteThread() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (threadId: string) =>
      api.DELETE<void>(`/threads/${threadId}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

export function useCancelThread() {
  return useMutation({
    mutationFn: (threadId: string) =>
      api.POST<void>(`/threads/${threadId}/cancel`, {}),
  });
}

interface ThreadQueryOpts {
  /**
   * Polling do historico persistido. Usado pelo ``ThreadTranscript``
   * de tasks em background: o run vive no worker (sem SSE aberto no
   * cliente), mas messages/steps sao persistidos incrementalmente.
   */
  refetchInterval?: number | false;
}

export function useThreadMessages(
  threadId: string,
  opts: ThreadQueryOpts = {},
) {
  return useQuery({
    queryKey: KEY_MSGS(threadId),
    queryFn: () => api.GET<Message[]>(`/threads/${threadId}/messages`),
    enabled: Boolean(threadId),
    refetchInterval: opts.refetchInterval,
  });
}

export function useThreadSteps(
  threadId: string,
  opts: ThreadQueryOpts = {},
) {
  return useQuery({
    queryKey: KEY_STEPS(threadId),
    queryFn: () => api.GET<StepsResponse>(`/threads/${threadId}/steps`),
    enabled: Boolean(threadId),
    refetchInterval: opts.refetchInterval,
  });
}

export function invalidateThreadCaches(qc: ReturnType<typeof useQueryClient>, threadId: string) {
  void qc.invalidateQueries({ queryKey: KEY_MSGS(threadId) });
  void qc.invalidateQueries({ queryKey: KEY_STEPS(threadId) });
  void qc.invalidateQueries({ queryKey: KEY_ITEM(threadId) });
  void qc.invalidateQueries({ queryKey: KEY });
}

// Re-export para conveniencia em outros features (ex.: agents/SandboxTab).
export type { ExecutionStep, Message, Thread };
